// Single source of truth for WoW connected-realm display names.
//
// The Go and Node services emit only realm IDs in API responses; this table
// is the only place that maps IDs to human-readable labels. If you need to
// add or rename a realm, do it here and only here.

export type RealmRegion = "us" | "eu";

export interface RealmInfo {
  id: number;
  region: RealmRegion;
  englishNames: string[];
  nativeNames?: string[];
}

// Classic Era connected realms. Each Classic realm has its own auction house
// (no cross-realm connections like retail). Populate these from the same
// Blizzard enumeration used to generate the Go realm-config JSON
// (flippingpal-processes/cmd/realmconfig). Until provisioned they are empty and
// the classic realm selector shows no options.
export const REALMS_CLASSIC_ERA_US: RealmInfo[] = [];
export const REALMS_CLASSIC_ERA_EU: RealmInfo[] = [];

export const REALMS_US: RealmInfo[] = [
  { id: 4, region: "us", englishNames: ["Kilrogg", "Winterhoof"] },
  { id: 5, region: "us", englishNames: ["Proudmoore"] },
  { id: 9, region: "us", englishNames: ["Kil'jaeden"] },
  { id: 11, region: "us", englishNames: ["Tichondrius"] },
  { id: 12, region: "us", englishNames: ["Silver Hand", "Thorium Brotherhood", "Farstriders"] },
  { id: 47, region: "us", englishNames: ["Eitrigg", "Shu'halo"] },
  { id: 52, region: "us", englishNames: ["Alleria", "Exodar", "Medivh", "Khadgar"] },
  { id: 53, region: "us", englishNames: ["Hellscream", "Gorefiend", "Spinebreaker", "Zangarmarsh", "Wildhammer", "Eredar"] },
  { id: 54, region: "us", englishNames: ["Blackhand", "Galakrond"] },
  { id: 55, region: "us", englishNames: ["Whisperwind", "Dentarg"] },
  { id: 57, region: "us", englishNames: ["Illidan"] },
  { id: 58, region: "us", englishNames: ["Stormreaver"] },
  { id: 60, region: "us", englishNames: ["Stormrage"] },
  { id: 61, region: "us", englishNames: ["Zul'jin"] },
  { id: 63, region: "us", englishNames: ["Durotan", "Ysera"] },
  { id: 64, region: "us", englishNames: ["Bloodhoof", "Duskwood"] },
  { id: 67, region: "us", englishNames: ["Elune", "Laughing Skull", "Auchindoun", "Cho'gall", "Gilneas"] },
  { id: 69, region: "us", englishNames: ["Arthas"] },
  { id: 71, region: "us", englishNames: ["Warsong", "Gorgonnash", "The Forgotten Coast", "Balnazzar", "Alterac Mountains", "Undermine", "Anvilmar"] },
  { id: 73, region: "us", englishNames: ["Bleeding Hollow"] },
  { id: 75, region: "us", englishNames: ["Argent Dawn", "The Scryers"] },
  { id: 76, region: "us", englishNames: ["Sargeras"] },
  { id: 77, region: "us", englishNames: ["Azgalor", "Thunderlord", "Destromath", "Blood Furnace", "Mannoroth", "Nazjatar", "Azshara"] },
  { id: 78, region: "us", englishNames: ["Magtheridon", "Anetheron", "Ysondre", "Altar of Storms"] },
  { id: 84, region: "us", englishNames: ["Dragonmaw", "Uldum", "Akama", "Korialstrasz", "Eldre'Thalas", "Mug'thol", "Antonidas"] },
  { id: 86, region: "us", englishNames: ["Silvermoon", "Skywall", "Terenas", "Hydraxis", "Drak'thul", "Borean Tundra", "Mok'Nathal", "Shadowsong"] },
  { id: 96, region: "us", englishNames: ["Eonar", "Skullcrusher", "Gul'dan", "Zuluhed", "Ursin", "Andorhal", "Black Dragonflight", "Velen", "Scilla"] },
  { id: 99, region: "us", englishNames: ["Llane", "Arygos"] },
  { id: 100, region: "us", englishNames: ["Earthen Ring"] },
  { id: 104, region: "us", englishNames: ["Malygos", "Garona", "Lightning's Blade", "Icecrown", "Onyxia", "Burning Blade"] },
  { id: 106, region: "us", englishNames: ["Aggramar", "Fizzcrank"] },
  { id: 113, region: "us", englishNames: ["Suramar", "Windrunner", "Darrowmere", "Draka"] },
  { id: 114, region: "us", englishNames: ["Dragonblight", "Fenris"] },
  { id: 115, region: "us", englishNames: ["Draenor", "Echo Isles"] },
  { id: 117, region: "us", englishNames: ["Bronzebeard", "Shandris"] },
  { id: 118, region: "us", englishNames: ["Feathermoon", "Scarlet Crusade"] },
  { id: 120, region: "us", englishNames: ["Darkspear"] },
  { id: 121, region: "us", englishNames: ["Azjol-Nerub", "Muradin", "Nordrassil", "Blackrock", "Khaz Modan"] },
  { id: 125, region: "us", englishNames: ["Shadow Council", "Sisters of Elune", "Cenarion Circle", "Blackwater Raiders"] },
  { id: 127, region: "us", englishNames: ["Firetree", "Drak'Tharon", "Rivendare", "Vashj", "Spirestone", "Malorne", "Frostwolf", "Stormscale"] },
  { id: 151, region: "us", englishNames: ["Runetotem", "Uther"] },
  { id: 154, region: "us", englishNames: ["Detheroc", "Dethecus", "Lethon", "Blackwing Lair", "Shadowmoon", "Haomarush"] },
  { id: 155, region: "us", englishNames: ["Kalecgos", "Shattered Halls", "Executus", "Deathwing"] },
  { id: 157, region: "us", englishNames: ["Dark Iron", "Shattered Hand", "Coilfang", "Demon Soul", "Dalvengyr"] },
  { id: 158, region: "us", englishNames: ["Greymane", "Tanaris"] },
  { id: 160, region: "us", englishNames: ["Staghelm", "Dawnbringer", "Madoran", "Azuremyst"] },
  { id: 162, region: "us", englishNames: ["Emerald Dream"] },
  { id: 163, region: "us", englishNames: ["Maelstrom", "Twisting Nether", "Lightninghoof", "The Venture Co", "Ravenholdt"] },
  { id: 1070, region: "us", englishNames: ["Alexstrasza", "Terokkar"] },
  { id: 1071, region: "us", englishNames: ["Kirin Tor", "Steamwheedle Cartel", "Sentinels"] },
  { id: 1072, region: "us", englishNames: ["Ravencrest", "Uldaman"] },
  { id: 1129, region: "us", englishNames: ["Agamaggan", "Kargath", "Burning Legion", "Thunderhorn", "The Underbog", "Blade's Edge", "Archimonde", "Norgannon", "Jaedenar"] },
  { id: 1136, region: "us", englishNames: ["Aegwynn", "Gurubashi", "Bonechewer", "Hakkar", "Garrosh", "Daggerspine"] },
  { id: 1138, region: "us", englishNames: ["Chromaggus", "Nathrezim", "Smolderthorn", "Anub'arak", "Arathor", "Garithos", "Drenden", "Crushridge"] },
  { id: 1147, region: "us", englishNames: ["Kul Tiras", "Bladefist"] },
  { id: 1151, region: "us", englishNames: ["Rexxar", "Misha"] },
  { id: 1168, region: "us", englishNames: ["Cairne", "Cenarius", "Frostmane", "Tortheldrin", "Ner'zhul", "Korgath", "Perenolde"] },
  { id: 1171, region: "us", englishNames: ["Wyrmrest Accord"] },
  { id: 1175, region: "us", englishNames: ["Trollbane", "Grizzly Hills", "Malfurion", "Lothar", "Kael'thas", "Gnomeregan", "Moonrunner", "Ghostlands"] },
  { id: 1184, region: "us", englishNames: ["Vek'nilash", "Nazgrel", "Nesingwary"] },
  { id: 1185, region: "us", englishNames: ["Sen'jin", "Dunemaul", "Maiev", "Bloodscalp", "Quel'dorei", "Boulderfist", "Stonemaul"] },
  { id: 1190, region: "us", englishNames: ["Baelgun", "Doomhammer"] },
  { id: 1425, region: "us", englishNames: ["Drakkari"] },
  { id: 1426, region: "us", englishNames: ["Aerie Peak"] },
  { id: 1427, region: "us", englishNames: ["Ragnaros"] },
  { id: 1428, region: "us", englishNames: ["Quel'Thalas"] },
  { id: 3207, region: "us", englishNames: ["Goldrinn"] },
  { id: 3208, region: "us", englishNames: ["Nemesis", "Tol Barad"] },
  { id: 3209, region: "us", englishNames: ["Azralon"] },
  { id: 3234, region: "us", englishNames: ["Gallywix"] },
  { id: 3661, region: "us", englishNames: ["Hyjal"] },
  { id: 3675, region: "us", englishNames: ["Moon Guard"] },
  { id: 3676, region: "us", englishNames: ["Area 52"] },
  { id: 3678, region: "us", englishNames: ["Thrall"] },
  { id: 3683, region: "us", englishNames: ["Dalaran"] },
  { id: 3684, region: "us", englishNames: ["Mal'Ganis"] },
  { id: 3685, region: "us", englishNames: ["Turalyon"] },
  { id: 3693, region: "us", englishNames: ["Kel'Thuzad"] },
  { id: 3694, region: "us", englishNames: ["Lightbringer"] },
  { id: 3721, region: "us", englishNames: ["Caelestrasz", "Nagrand", "Saurfang"] },
  { id: 3723, region: "us", englishNames: ["Barthilas"] },
  { id: 3725, region: "us", englishNames: ["Frostmourne", "Dreadmaul", "Thaurissan", "Jubei'Thos", "Gundrak"] },
  { id: 3726, region: "us", englishNames: ["Khaz'goroth", "Aman'Thul", "Dath'Remar"] },
];

export const REALMS_EU: RealmInfo[] = [
  { id: 509, region: "eu", englishNames: ["Garona", "Sargeras", "Ner'zhul"] },
  { id: 510, region: "eu", englishNames: ["Vol'jin", "Chants éternels"] },
  { id: 512, region: "eu", englishNames: ["Arak-arahm", "Rashgarroth", "Kael'thas", "Throk'Feroth"] },
  { id: 531, region: "eu", englishNames: ["Dethecus", "Theradras", "Onyxia", "Mug'thol", "Terrordar"] },
  { id: 570, region: "eu", englishNames: ["Lothar", "Baelgun", "Azshara", "Krag'jin"] },
  { id: 578, region: "eu", englishNames: ["Arthas", "Blutkessel", "Wrathbringer", "Durotan", "Kel'Thuzad", "Vek'lor", "Tirion"] },
  { id: 580, region: "eu", englishNames: ["Blackmoore", "Tichondrius", "Lordaeron"] },
  { id: 581, region: "eu", englishNames: ["Blackrock"] },
  { id: 604, region: "eu", englishNames: ["Thrall", "Kargath", "Ambossar"] },
  { id: 612, region: "eu", englishNames: ["Nefarian", "Gilneas", "Destromath", "Ulduar", "Mannoroth", "Gorgonnash", "Nera'thor"] },
  { id: 633, region: "eu", englishNames: ["Kor'gall", "Executus", "Shattered Hand", "Bloodfeather", "Terokkar", "Saurfang", "Darkspear", "Burning Steppes"] },
  { id: 1080, region: "eu", englishNames: ["Khadgar", "Bloodhoof"] },
  { id: 1082, region: "eu", englishNames: ["Kul Tiras", "Alonsus", "Anachronos"] },
  { id: 1084, region: "eu", englishNames: ["Dentarg", "Tarren Mill"] },
  { id: 1085, region: "eu", englishNames: ["Moonglade", "Steamwheedle Cartel", "The Sha'tar"] },
  { id: 1091, region: "eu", englishNames: ["Emeriss", "Twilight's Hammer", "Bloodscalp", "Crushridge", "Agamaggan", "Hakkar"] },
  { id: 1092, region: "eu", englishNames: ["Drak'thul", "Burning Blade"] },
  { id: 1096, region: "eu", englishNames: ["Scarshield Legion", "Sporeggar", "Earthen Ring", "Defias Brotherhood", "Ravenholdt", "Darkmoon Faire", "The Venture Co"] },
  { id: 1097, region: "eu", englishNames: ["Ysera", "Malorne"] },
  { id: 1098, region: "eu", englishNames: ["Malygos", "Malfurion"] },
  { id: 1099, region: "eu", englishNames: ["Rexxar", "Alleria"] },
  { id: 1104, region: "eu", englishNames: ["Anetheron", "Kil'jaeden", "Rajaxx", "Festung der Stürme", "Gul'dan", "Nathrezim"] },
  { id: 1105, region: "eu", englishNames: ["Nazjatar", "Zuluhed", "Dalvengyr", "Aman'Thul", "Frostmourne", "Anub'arak"] },
  { id: 1121, region: "eu", englishNames: ["Die Arguswacht", "Die ewige Wacht", "Die Todeskrallen", "Das Syndikat", "Der abyssische Rat", "Kult der Verdammten", "Das Konsortium", "Die Silberne Hand"] },
  { id: 1122, region: "eu", englishNames: ["Uldaman", "Drek'Thar", "Krasus", "Eitrigg"] },
  { id: 1127, region: "eu", englishNames: ["Confrérie du Thorium", "La Croisade écarlate", "Culte de la Rive noire", "Les Sentinelles", "Kirin Tor", "Les Clairvoyants", "Conseil des Ombres"] },
  { id: 1301, region: "eu", englishNames: ["Outland"] },
  { id: 1302, region: "eu", englishNames: ["Archimonde"] },
  { id: 1303, region: "eu", englishNames: ["Grim Batol", "Aggra (Português)", "Frostmane"] },
  { id: 1305, region: "eu", englishNames: ["Kazzak"] },
  { id: 1307, region: "eu", englishNames: ["Chamber of Aspects"] },
  { id: 1309, region: "eu", englishNames: ["Pozzo dell'Eternità"] },
  { id: 1313, region: "eu", englishNames: ["Wildhammer", "Thunderhorn"] },
  { id: 1315, region: "eu", englishNames: ["Elune", "Varimathras"] },
  { id: 1316, region: "eu", englishNames: ["Nemesis"] },
  { id: 1325, region: "eu", englishNames: ["Aggramar", "Hellscream"] },
  { id: 1329, region: "eu", englishNames: ["Ravencrest"] },
  { id: 1331, region: "eu", englishNames: ["Suramar", "Medivh"] },
  { id: 1335, region: "eu", englishNames: ["Ysondre"] },
  { id: 1378, region: "eu", englishNames: ["Dun Modr", "C'Thun"] },
  { id: 1379, region: "eu", englishNames: ["Zul'jin", "Uldum", "Sanguino", "Shen'dralar"] },
  { id: 1384, region: "eu", englishNames: ["Tyrande", "Los Errantes", "Colinas Pardas"] },
  { id: 1385, region: "eu", englishNames: ["Exodar", "Minahonda"] },
  { id: 1388, region: "eu", englishNames: ["Lightbringer", "Mazrigos"] },
  { id: 1390, region: "eu", englishNames: ["Hyjal"] },
  { id: 1393, region: "eu", englishNames: ["Bronze Dragonflight", "Nordrassil"] },
  { id: 1396, region: "eu", englishNames: ["Quel'Thalas", "Azjol-Nerub"] },
  { id: 1400, region: "eu", englishNames: ["Un'Goro", "Sen'jin", "Area 52"] },
  { id: 1401, region: "eu", englishNames: ["Garrosh", "Shattrath", "Nozdormu", "Perenolde", "Teldrassil"] },
  { id: 1402, region: "eu", englishNames: ["Turalyon", "Doomhammer"] },
  { id: 1403, region: "eu", englishNames: ["Draenor"] },
  { id: 1405, region: "eu", englishNames: ["Todeswache", "Zirkel des Cenarius", "Der Mithrilorden", "Forscherliga", "Der Rat von Dalaran", "Die Nachtwache"] },
  { id: 1406, region: "eu", englishNames: ["Arygos", "Khaz'goroth"] },
  { id: 1408, region: "eu", englishNames: ["Norgannon", "Dun Morogh"] },
  { id: 1416, region: "eu", englishNames: ["Blade's Edge", "Eonar", "Vek'nilash", "Aerie Peak", "Bronzebeard"] },
  { id: 1417, region: "eu", englishNames: ["Azuremyst", "Stormrage"] },
  { id: 1587, region: "eu", englishNames: ["Hellfire", "Runetotem", "Arathor", "Kilrogg", "Nagrand"] },
  { id: 1596, region: "eu", englishNames: ["Karazhan", "Dragonblight", "The Maelstrom", "Ghostlands", "Lightning's Blade", "Deathwing"] },
  { id: 1597, region: "eu", englishNames: ["Auchindoun", "Dunemaul", "Sylvanas", "Jaedenar"] },
  { id: 1598, region: "eu", englishNames: ["Shattered Halls", "Chromaggus", "Sunstrider", "Balnazzar", "Talnivarr", "Ahn'Qiraj", "Daggerspine", "Laughing Skull", "Trollbane", "Boulderfist"] },
  { id: 1602, region: "eu", englishNames: ["Gordunni"], nativeNames: ["Гордунни"] },
  { id: 1604, region: "eu", englishNames: ["Soulflayer"], nativeNames: ["Свежеватель Душ"] },
  { id: 1605, region: "eu", englishNames: ["Deathguard"], nativeNames: ["Страж Смерти"] },
  { id: 1614, region: "eu", englishNames: ["Galakrond", "Deepholm", "Razuvious"], nativeNames: ["Галакронд", "Подземье", "Разувий"] },
  { id: 1615, region: "eu", englishNames: ["Howling Fjord"], nativeNames: ["Ревущий фьорд"] },
  { id: 1618, region: "eu", englishNames: ["Die Aldor"] },
  { id: 1621, region: "eu", englishNames: ["Marécage de Zangar", "Cho'gall", "Eldre'Thalas", "Sinstralis", "Dalaran"] },
  { id: 1623, region: "eu", englishNames: ["Fordragon"], nativeNames: ["Дракономор"] },
  { id: 1624, region: "eu", englishNames: ["Naxxramas", "Arathi", "Illidan", "Temple noir"] },
  { id: 1922, region: "eu", englishNames: ["Azuregos"], nativeNames: ["Азурегос"] },
  { id: 1923, region: "eu", englishNames: ["Ashenvale"], nativeNames: ["Ясеневый лес"] },
  { id: 1925, region: "eu", englishNames: ["Eversong"], nativeNames: ["Вечная Песня"] },
  { id: 1928, region: "eu", englishNames: ["Goldrinn", "Lich King", "Greymane"], nativeNames: ["Голдринн", "Король-лич", "Седогрив"] },
  { id: 1929, region: "eu", englishNames: ["Blackscar", "Deathweaver", "Borean Tundra", "Thermaplugg", "Grom", "Booty Bay"], nativeNames: ["Черный Шрам", "Ткач Смерти", "Борейская тундра", "Термоштепсель", "Гром", "Пиратская Бухта"] },
  { id: 2073, region: "eu", englishNames: ["Stormscale"] },
  { id: 2074, region: "eu", englishNames: ["Terenas", "Emerald Dream"] },
  { id: 3391, region: "eu", englishNames: ["Silvermoon"] },
  { id: 3656, region: "eu", englishNames: ["Spinebreaker", "Dragonmaw", "Vashj", "Stormreaver", "Haomarush"] },
  { id: 3657, region: "eu", englishNames: ["Genjuros", "Zenedar", "Bladefist", "Neptulon", "Frostwhisper", "Darksorrow"] },
  { id: 3666, region: "eu", englishNames: ["Shadowsong", "Aszune"] },
  { id: 3674, region: "eu", englishNames: ["Twisting Nether"] },
  { id: 3679, region: "eu", englishNames: ["Aegwynn"] },
  { id: 3681, region: "eu", englishNames: ["Magtheridon"] },
  { id: 3682, region: "eu", englishNames: ["Ragnaros"] },
  { id: 3686, region: "eu", englishNames: ["Antonidas"] },
  { id: 3690, region: "eu", englishNames: ["Khaz Modan"] },
  { id: 3691, region: "eu", englishNames: ["Echsenkessel", "Blackhand", "Mal'Ganis", "Taerar"] },
  { id: 3692, region: "eu", englishNames: ["Eredar"] },
  { id: 3696, region: "eu", englishNames: ["Nethersturm", "Alexstrasza", "Madmortem", "Proudmoore"] },
  { id: 3702, region: "eu", englishNames: ["Argent Dawn"] },
  { id: 3703, region: "eu", englishNames: ["Frostwolf"] },
  { id: 3713, region: "eu", englishNames: ["Al'Akir", "Skullcrusher", "Xavius", "Burning Legion"] },
];

const REALMS_BY_REGION: Record<RealmRegion, RealmInfo[]> = {
  us: REALMS_US,
  eu: REALMS_EU,
};

const REALM_INDEX: Record<RealmRegion, Map<number, RealmInfo>> = {
  us: new Map(REALMS_US.map((r) => [r.id, r])),
  eu: new Map(REALMS_EU.map((r) => [r.id, r])),
};

// Region-agnostic index. Safe because US and EU connected-realm IDs do not
// overlap (verified). Used by display surfaces that don't carry the region
// alongside the realm ID.
const REALM_INDEX_ANY: Map<number, RealmInfo> = new Map([
  ...REALMS_US.map((r) => [r.id, r] as const),
  ...REALMS_EU.map((r) => [r.id, r] as const),
]);

export function getRealmsForRegion(region: RealmRegion): RealmInfo[] {
  return REALMS_BY_REGION[region];
}

export function getRealmInfo(id: number, region: RealmRegion): RealmInfo | undefined {
  return REALM_INDEX[region].get(id);
}

// Display label for UI surfaces (table cells, dropdowns, tooltips, CSV).
// Russian realms render as "Native (English), Native (English), ..." per realm.
// Falls back to "Realm <id>" if the ID is unknown.
export function getRealmDisplayLabel(id: number, region: RealmRegion): string {
  const info = getRealmInfo(id, region);
  if (!info) return `Realm ${id}`;
  if (info.nativeNames && info.nativeNames.length === info.englishNames.length) {
    return info.englishNames
      .map((en, i) => `${info.nativeNames![i]} (${en})`)
      .join(", ");
  }
  return info.englishNames.join(", ");
}

// Bare English label, used wherever the value must be addon-friendly: Auctionator
// clipboard, URL slugs (undermine.exchange / wowhead), sort keys. Always returns
// the English names so Russian realms stay searchable in addons that expect them.
export function getRealmEnglishLabel(id: number, region: RealmRegion, separator = ", "): string {
  const info = getRealmInfo(id, region);
  if (!info) return `Realm ${id}`;
  return info.englishNames.join(separator);
}

export function isRussianRealm(id: number, region: RealmRegion): boolean {
  const info = getRealmInfo(id, region);
  return !!info?.nativeNames;
}

// Region-agnostic variants — use these on display surfaces that receive only
// the realm ID (e.g. table rows, CSV export, clipboard).
export function resolveRealmDisplayLabel(id: number): string {
  const info = REALM_INDEX_ANY.get(id);
  if (!info) return `Realm ${id}`;
  if (info.nativeNames && info.nativeNames.length === info.englishNames.length) {
    return info.englishNames
      .map((en, i) => `${info.nativeNames![i]} (${en})`)
      .join(", ");
  }
  return info.englishNames.join(", ");
}

export function resolveRealmEnglishLabel(id: number, separator = ", "): string {
  const info = REALM_INDEX_ANY.get(id);
  if (!info) return `Realm ${id}`;
  return info.englishNames.join(separator);
}
